import { createContext, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';

import { GetPackagesResult, useGetPackages } from 'api/hooks/public/useGetPackages';
import { useGetSchoolInfo } from 'api/hooks/public/useGetSchoolInfo';

export interface Course {
  title: string;
  description: string;
  requirements: string[];
}

// export interface Package {
//     id: string;
//     title: string;
//     price: string;
//     description?: string;
//     locations: {
//         id: string;
//         name: string;
//         remCapacity: string;
//     }[];
//     includes: string[];
//     isHighlighted: boolean;
// }

export interface SchoolLocation {
  id: string;
  name: string;
  description?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

export interface Testimonial {
  id: string;
  author: string;
  authorSubtitle?: string;
  content: string;
  rating?: number;
}

export interface AboutPageData {
  title?: string;
  subtitle?: string;
  bannerImage?: string;
  mainSectionTitle: string;
  mainSectionContent: string;
  mainSectionImage?: string;
  subsections?: {
    title: string;
    content: string;
  }[];
  teamMembers?: {
    name: string;
    title?: string;
    avatar?: string;
    about?: string;
  }[];
}

export interface FaqPageData {
  title?: string;
  subtitle?: string;
  bannerImage?: string;
  categories: {
    name: string;
    questions: {
      question: string;
      answer: string;
    }[];
  }[];
}

export interface SchoolContextData {
  name: string;
  shortName?: string;
  missionStatement?: string;
  logoImage?: string;
  bannerImage?: string;
  loginImage?: string;
  locations?: SchoolLocation[];
  testimonials?: Testimonial[];
  coursesRequiredForLicense: Course[];
  packages: GetPackagesResult[];
  about: AboutPageData;
  faq: FaqPageData;
}

const SchoolContext = createContext<SchoolContextData | null | undefined>(undefined);

interface PropTypes {
  children: JSX.Element;
}

function SchoolProvider({ children }: PropTypes) {
  const [schoolContext, setSchoolContext] = useState<SchoolContextData | null>(null);
  const { data: schoolInfo, isSuccess: isSchoolInfoSuccess } = useGetSchoolInfo();
  const { data: packages } = useGetPackages();

  useEffect(() => {
    if (isSchoolInfoSuccess) {
      const urlsToPreload = [schoolInfo?.logo_image, schoolInfo?.banner_image, schoolInfo?.login_image].filter(
        (url): url is string => Boolean(url),
      ); // remove undefined/null

      urlsToPreload.forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    }

    setSchoolContext({
      name: schoolInfo?.name ?? '',
      shortName: schoolInfo?.short_name,
      missionStatement: schoolInfo?.mission_statement,
      logoImage: schoolInfo?.logo_image,
      bannerImage: schoolInfo?.banner_image,
      loginImage: schoolInfo?.login_image,
      packages: packages ?? [],
      locations: schoolInfo?.locations?.map(({ street_address, ...rest }) => ({
        streetAddress: street_address,
        ...rest,
      })),
      testimonials: schoolInfo?.testimonials,
      coursesRequiredForLicense: [
        {
          title: 'Traditional Or Online Drivers Education',
          description:
            'Driver education is a foundation course, teaching the theory of responsible driving. Emphasis is placed upon the introduction of the mechanics of driving, execution of most driving operations, and a thorough understanding of the rules of safe driving. The Teen Driver Education course consists of 30 classroom hours.',
          requirements: ['Students must be at least 15 years of age by the first day of class.'],
        },
        {
          title: 'Behind The Wheel Driving Lessons',
          description:
            'Our six-hour Behind-the-Wheel program with IN certified instructors offers the student a variety of driving experiences in a controlled, safe environment. Driving experiences include city, rural, interstate, and - the dreaded - parallel parking practice!',
          requirements: [
            'The CDE form, needed to obtain a permit, will be issued once the online classroom portion has been completed or you have registered for an in-person class.',
          ],
        },
        {
          title: 'Driving Skills Test',
          description:
            'The driving skills test is required of all students in order to obtain a license and can be taken at our location, with one of our certified instructors. Students will use our car and instructor, and will not be required to take the test with the BMV. Students must be at least 16 years of age and have their 50 hour driving log completed.',
          requirements: [
            'Completed 30 hours of classroom/online training',
            'Completed 6 hours of behind the wheel training',
            'Students must be at least 16 years of age',
            'Students must have their 50 hour driving log completed',
          ],
        },
      ],
      about: {
        title: schoolInfo?.about.title,
        subtitle: schoolInfo?.mission_statement,
        bannerImage: schoolInfo?.about?.banner_image,
        mainSectionTitle: schoolInfo?.about?.main_section_title ?? '',
        mainSectionContent: schoolInfo?.about?.main_section_content ?? '',
        mainSectionImage: schoolInfo?.about.main_section_image ?? schoolInfo?.logo_image,
        subsections: schoolInfo?.about?.subsections,
        teamMembers:
          schoolInfo?.about?.team?.map((teamMember) => ({
            avatar: teamMember.photo,
            name: teamMember.name,
            title: teamMember.title,
            about: teamMember.bio?.content,
          })) ?? [],
      },
      faq: {
        title: 'Have a question?',
        subtitle: 'Search our FAQ for answers to anything you might ask.',
        bannerImage: undefined,
        categories: [
          {
            name: 'General Information',
            questions: [
              {
                question:
                  'Are all instructors certified by the State of Indiana and the Indiana Bureau of Motor Vehicles?',
                answer: 'Yes',
              },
              {
                question: 'Are you able to see licenses of the instructors?',
                answer: 'Yes',
              },
              {
                question: 'Are all vehicles dual controlled?',
                answer: 'Yes, with a brake.',
              },
              {
                question: 'Are all vehicles equipped with in-car cameras?',
                answer: 'Yes. All drivers ed cars have in-car cameras installed and are used on each driving session.',
              },
            ],
          },
          {
            name: 'Student',
            questions: [
              {
                question: 'When can I start driving in the car with my parents?',
                answer: `If you are enrolled in a driver education course, and have obtained your learner's permit, you can legally drive with your parents. If you are at least 16 years old and not taking a driver education course, once you have received your learner's permit, you can start practice driving with your parents.`,
              },
              {
                question:
                  'If I start driver education and get my permit, then stop going to driver education (or stop doing an online course) can I keep driving on my permit?',
                answer: `The online programs and the licensed schools are required to keep track of your progress in the course. If you stop attending (or participating online), they will first try to contact you to find out why. If they can't contact you, or if you don't respond, they will contact the BMV and have your permit invalidated. If you continue to drive and are stopped by law enforcement or are involved in a collision, you could be cited for operating a vehicle without a valid license. This could also affect your ability to get a new permit/license.`,
              },
              {
                question:
                  'If I start a driver education course with one school, then decide to switch to another school, can I transfer the work that I have completed or do I have to start over?',
                answer:
                  'Current rules allow for the transfer of classroom instruction from one school to another where the behind the wheel phase will be completed. However, this is contingent upon the second school accepting the course work and records from the first school. The second school then becomes the school of record.',
              },
              {
                question:
                  'If I take an online driver education classroom course, can I drive with my parents once I start that course? Can I drive with them when I finish the course?',
                answer: `Effective July 23, 2019, online programs will no longer be issuing the CDE form needed to obtain the permit. You must complete the online portion of the course and provide the certificate of completion to a licensed driver education school. Once you have provided the correct documentation and have registered and paid for the behind the wheel portion of the course, the driving school will issue the CDE form and you will be able to then get your permit. You cannot legally drive until you have obtained your learner's permit.`,
              },
            ],
          },
        ],
      },
    });
  }, [schoolInfo, packages, isSchoolInfoSuccess]);

  return (
    <SchoolContext.Provider value={schoolContext}>
      <Helmet>
        {/* Global site favicon */}
        <link rel="icon" href={schoolInfo?.logo_image ?? '/dd-logo.png'} type="image/png" />
      </Helmet>
      {children}
    </SchoolContext.Provider>
  );
}

export { SchoolContext, SchoolProvider };
